<template>
  <div
    class="wrapper container-fluid d-flex justify-content-center"
    :style="{ marginLeft: isMobile ? '27px' : '' }"
    id="app"
  >
    <div class="content-container" style="max-width: 1200px">
      <Layout :user="user"></Layout>
      <section class="container py-4">
        <div class="row text-center mb-4">
          <div class="col-12">
            <h1 class="fw-bold text-brand-purple">My Activities</h1>
            <p class="fst-italic text-muted mb-0">
              Stay updated and in the know on all your activities
            </p>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="row justify-content-center">
          <div class="col-md-8 text-center py-5">
            <div class="spinner-border text-brand-purple mb-3" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted">Loading your activities...</p>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="hasError" class="row justify-content-center">
          <div class="col-md-8">
            <div class="card border-0 shadow text-center p-5">
              <div class="mb-4">
                <div
                  class="bg-danger bg-opacity-10 rounded-circle mx-auto d-flex align-items-center justify-content-center"
                  style="width: 80px; height: 80px"
                >
                  <i
                    class="fas fa-exclamation-triangle text-danger"
                    style="font-size: 32px"
                  ></i>
                </div>
              </div>
              <h3 class="text-danger mb-3">Error Loading Activities</h3>
              <p class="text-muted mb-4">{{ errorMessage }}</p>
              <button
                @click="retryLoading"
                class="btn btn-brand-purple text-white px-4 py-2 mx-auto"
                style="max-width: 200px"
              >
                <i class="fas fa-sync-alt me-2"></i>
                Try Again
              </button>
            </div>
          </div>
        </div>

        <!-- No Activities Message -->
        <div v-else-if="!activities.length" class="row justify-content-center">
          <div class="col-md-8">
            <div class="card border-0 shadow text-center p-5">
              <div class="mb-4">
                <div
                  class="bg-brand-purple-light rounded-circle mx-auto d-flex align-items-center justify-content-center"
                  style="width: 80px; height: 80px"
                >
                  <i
                    class="fas fa-calendar-alt text-brand-purple"
                    style="font-size: 32px"
                  ></i>
                </div>
              </div>
              <h3 class="text-brand-purple mb-3">No Activities Yet</h3>
              <p class="text-muted mb-4">
                You're not currently enrolled in any camps, sessions, or
                memberships.
              </p>
              <button
                class="btn btn-brand-green text-white px-4 py-2 mx-auto"
                style="max-width: 200px"
              >
                <i class="fas fa-search me-2"></i>
                Find Activities
              </button>
            </div>
          </div>
        </div>

        <!-- Activities List -->
        <div v-else class="row g-4 justify-content-center">
          <div
            v-for="activity in activities"
            :key="activity._id"
            class="col-12"
          >
            <div
              class="card border-0 shadow h-100 activity-card mx-auto"
              style="max-width: 1400px"
            >
              <!-- Logo Display -->
              <div
                class="card-img-top text-center"
                style="background-color: #000000; height: 200px"
              >
                <img
                  v-if="activity.campLogo"
                  :src="activity.campLogo"
                  :alt="activity.campName + ' logo'"
                  class="img-fluid w-100 h-100"
                  style="object-fit: cover"
                />
                <img
                  v-else-if="activity.membershipLogo"
                  :src="activity.membershipLogo"
                  :alt="activity.campName + ' membership logo'"
                  class="img-fluid w-100 h-100"
                  style="object-fit: cover"
                />
                <div
                  v-else
                  class="d-flex align-items-center justify-content-center w-100"
                  style="background-color: #f8f9fa; height: 200px"
                >
                  <div class="text-center">
                    <div class="mb-3">
                      <i
                        class="fas fa-camera text-brand-purple"
                        style="font-size: 3.5rem; opacity: 0.7"
                      ></i>
                    </div>
                    <h5 class="text-brand-purple mb-0" style="opacity: 0.7">
                      {{ activity.campName || 'Activity' }}
                    </h5>
                    <p class="text-muted small mt-2">No image available</p>
                  </div>
                </div>
              </div>
              <div
                class="card-header bg-brand-green"
                style="height: 8px; padding: 0"
              ></div>
              <div class="card-body p-4">
                <h3 class="card-title fw-bold text-brand-purple mb-3">
                  {{ activity.campName }}
                </h3>

                <div class="mb-4">
                  <div class="d-flex align-items-center mb-2">
                    <div class="bg-brand-purple-light rounded-circle p-2 me-3">
                      <i class="fas fa-map-marker-alt text-brand-purple"></i>
                    </div>
                    <div>
                      <p class="mb-0">
                        {{ activity.campLocation || 'Location not specified' }}
                      </p>
                    </div>
                  </div>

                  <div class="d-flex align-items-center mb-2">
                    <div class="bg-brand-purple-light rounded-circle p-2 me-3">
                      <i class="fas fa-calendar text-brand-purple"></i>
                    </div>
                    <div>
                      <p
                        class="mb-0"
                        v-html="formatDates(activity.campDates)"
                      ></p>
                    </div>
                  </div>

                  <div class="d-flex align-items-start mb-2">
                    <div
                      class="bg-brand-purple-light rounded-circle p-2 me-3 mt-1"
                    >
                      <i class="fas fa-info-circle text-brand-purple"></i>
                    </div>
                    <div>
                      <p class="mb-0">
                        {{
                          activity.campDescription || 'No description available'
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Additional Details Section -->
                <div v-if="activity.additionalDetails" class="mb-4">
                  <h4 class="fs-5 fw-semibold text-brand-purple mb-2">
                    Additional Details
                  </h4>
                  <p class="mb-0">{{ activity.additionalDetails }}</p>
                </div>

                <!-- Coach Information -->
                <div v-if="activity.coach" class="mb-4 p-3 bg-light rounded">
                  <h4 class="fs-5 fw-semibold text-brand-purple mb-2">
                    Coach Information
                  </h4>
                  <div class="d-flex align-items-center">
                    <div class="bg-brand-green-light rounded-circle p-2 me-3">
                      <i class="fas fa-user-tie text-brand-green"></i>
                    </div>
                    <div>
                      <p class="fw-medium mb-0">
                        {{ activity.coach.name || 'Coach name not available' }}
                      </p>
                      <p
                        class="small text-muted mb-0"
                        v-if="activity.coach.title"
                      >
                        {{ activity.coach.title }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Get in Touch Button -->
                <div class="mt-4 pt-3 border-top">
                  <a
                    @click="
                      getInTouch(
                        activity.coachEmail,
                        activity.coachFirstName + ' ' + activity.coachLastName,
                      )
                    "
                    class="btn btn-brand-green text-white w-100 py-2"
                  >
                    <i class="fas fa-envelope me-2"></i>
                    Contact Coach
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination for large number of activities -->
        <div v-if="allActivities.length > itemsPerPage" class="row mt-4">
          <div class="col-12 d-flex justify-content-center">
            <nav aria-label="Activities pagination">
              <ul class="pagination">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="
                      currentPage > 1 && changePage(currentPage - 1)
                    "
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </a>
                </li>
                <li
                  v-for="page in Math.ceil(allActivities.length / itemsPerPage)"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(page)"
                    >{{ page }}</a
                  >
                </li>
                <li
                  class="page-item"
                  :class="{
                    disabled:
                      currentPage ===
                      Math.ceil(allActivities.length / itemsPerPage),
                  }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="
                      currentPage <
                        Math.ceil(allActivities.length / itemsPerPage) &&
                        changePage(currentPage + 1)
                    "
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'
import Layout from '../components/Layout.vue'

export default {
  components: {
    Layout,
  },
  data() {
    return {
      user: {},
      activities: [],
      isLoading: true,
      hasError: false,
      errorMessage: '',
      itemsPerPage: 9,
      currentPage: 1,
      allActivities: [], // Store all activities for pagination
    }
  },
  methods: {
    // Format dates to show in a readable format
    formatDates(dates) {
      if (!dates || !Array.isArray(dates) || dates.length === 0) {
        return 'Dates not specified'
      }

      // Sort dates by start time
      const sortedDates = [...dates].sort(
        (a, b) => new Date(a.start) - new Date(b.start),
      )

      // Format each date range
      const formattedDates = sortedDates.map(date => {
        const start = new Date(date.start)
        const end = new Date(date.end)

        // Format the date and time
        const startDate = start.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
        const startTime = start.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        const endTime = end.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })

        return `${startDate} (${startTime} - ${endTime})`
      })

      // If there are multiple dates, join with HTML line breaks
      if (formattedDates.length > 1) {
        return formattedDates.join('<br>')
      }

      return formattedDates[0]
    },
    async getInTouch(email, name) {
      this.$router.push(
        '/Recruit/Dashboard/Inbox?email=' + email + '&name=' + name,
      )
    },

    async getActivities() {
      this.isLoading = true
      this.hasError = false

      try {
        let url =
          import.meta.env.VITE_APP_DB_URL +
          'recruit/getActivities?id=' +
          this.user._id

        const response = await axios.get(url, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('jwt'),
          },
        })

        // Store all activities and set up pagination
        this.allActivities = response.data.activities?.reverse() || []
        this.updatePaginatedActivities()

        console.log('Activities loaded:', this.allActivities.length)
      } catch (err) {
        console.error('Error loading activities:', err)
        this.hasError = true
        this.errorMessage =
          err.response?.data?.errorMessage ||
          'Failed to load activities. Please try again.'
      } finally {
        this.isLoading = false
      }
    },

    updatePaginatedActivities() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage
      const endIndex = startIndex + this.itemsPerPage
      this.activities = this.allActivities?.slice(startIndex, endIndex)
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.updatePaginatedActivities()

      // Scroll to top of activities section
      window.scrollTo({
        top: document.querySelector('section').offsetTop,
        behavior: 'smooth',
      })
    },

    getUserDetails() {
      this.user = JSON.parse(localStorage.getItem('user'))
    },

    getContactLink(activity) {
      // This function should return the same link that was used in the modal
      // Replace this with the actual link generation logic from your modal component
      return (
        activity.contactLink ||
        `mailto:${activity.coachEmail || 'coach@example.com'}?subject=Regarding ${activity.campName}`
      )
    },

    retryLoading() {
      this.getActivities()
    },
  },

  async created() {
    this.getUserDetails()
    await this.getActivities()
  },

  computed: {
    totalPages() {
      return Math.ceil(this.allActivities.length / this.itemsPerPage)
    },
    isMobile() {
      var check = false
      ;(function (a) {
        if (
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
            a,
          ) ||
          /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
            a.substr(0, 4),
          )
        )
          check = true
      })(navigator.userAgent || navigator.vendor || window.opera)
      return check
    },
  },
}
</script>

<style scoped>
/* Brand Colors */
:root {
  --brand-green: #19d412;
  --brand-purple: #363166;
  --brand-dark: #030021;
  --brand-green-light: rgba(25, 212, 18, 0.15);
  --brand-purple-light: rgba(54, 49, 102, 0.15);
}

/* Text Colors */
.text-brand-green {
  color: #19d412 !important;
}

.text-brand-purple {
  color: #363166 !important;
}

.text-brand-dark {
  color: #030021 !important;
}

/* Background Colors */
.bg-brand-green {
  background-color: #19d412 !important;
}

.bg-brand-purple {
  background-color: #363166 !important;
}

.bg-brand-dark {
  background-color: #030021 !important;
}

.bg-brand-green-light {
  background-color: rgba(25, 212, 18, 0.15) !important;
}

.bg-brand-purple-light {
  background-color: rgba(54, 49, 102, 0.15) !important;
}

/* Button Styles */
.btn-brand-green {
  background-color: #19d412;
  border-color: #19d412;
}

.btn-brand-green:hover {
  background-color: #16b910;
  border-color: #16b910;
}

.btn-brand-purple {
  background-color: #363166;
  border-color: #363166;
  color: white;
}

.btn-brand-purple:hover {
  background-color: #2a2650;
  border-color: #2a2650;
  color: white;
}

/* Card Styles */
.card {
  transition: all 0.3s ease;
  border-radius: 0.5rem;
}

.activity-card {
  transition: all 0.3s ease-in-out;
}

.activity-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.shadow {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Utility Classes */
.fw-medium {
  font-weight: 500;
}

/* Pagination Styling */
.pagination .page-item.active .page-link {
  background-color: #363166;
  border-color: #363166;
}

.pagination .page-link {
  color: #363166;
}

.pagination .page-link:focus {
  box-shadow: 0 0 0 0.25rem rgba(54, 49, 102, 0.25);
}
</style>
